import { useCustom, useCustomMutation, useGetIdentity } from "@refinedev/core";
import {
  Alert,
  Button,
  Card,
  Form,
  InputNumber,
  Modal,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import { useState } from "react";
import { apiUrl } from "@/consts";

type MembershipFee = {
  id: string;
  name: string;
  status: string;
  seasonFeePercent: number | null;
  effectivePercent: number;
  source: "membership" | "club" | "platform";
};
type FeeStructure = {
  globalPercent: number;
  clubPercent: number | null;
  inheritedPercent: number;
  memberships: MembershipFee[];
};

export function MembershipFees({ clubId }: { clubId: string }) {
  const { data: identity } = useGetIdentity<{ role: string }>();
  const { data, isLoading, isError, refetch } = useCustom<FeeStructure>({
    url: `${apiUrl}/clubs/${clubId}/membership-fees`,
    method: "get",
  });
  const fees = data?.data;
  const [editing, setEditing] = useState<MembershipFee | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [form] = Form.useForm<{ seasonFeePercent: number | null }>();
  const { mutate, isLoading: saving } = useCustomMutation();

  function save(seasonFeePercent: number | null) {
    if (!editing) return;
    setSaveError(null);
    mutate(
      {
        url: `${apiUrl}/clubs/${clubId}/membership-fees/${editing.id}`,
        method: "patch",
        values: { seasonFeePercent },
      },
      {
        onSuccess: () => {
          message.success("Membership commission updated");
          setEditing(null);
          void refetch();
        },
        onError: () =>
          setSaveError("Could not update the commission. Please try again."),
      },
    );
  }

  return (
    <Card title="Membership fee overrides" size="small">
      <Space direction="vertical" style={{ width: "100%" }}>
        <Typography.Paragraph type="secondary">
          Platform commission on the membership price, separate from card
          processing fees.
          {fees &&
            ` Club default: ${fees.inheritedPercent}%${fees.clubPercent == null ? " (platform default)" : ""}.`}{" "}
          Changes apply to new checkouts. Existing payment schedules keep their
          agreed rate. Family bundles use the bundle membership’s rate.
        </Typography.Paragraph>
        {isError && (
          <Alert
            type="error"
            showIcon
            message="Could not load membership fees"
            action={<Button onClick={() => void refetch()}>Retry</Button>}
          />
        )}
        <Table<MembershipFee>
          rowKey="id"
          size="small"
          loading={isLoading}
          dataSource={fees?.memberships ?? []}
          locale={{ emptyText: "No membership plans" }}
          pagination={{ pageSize: 10, hideOnSinglePage: true }}
          columns={[
            { title: "Membership", dataIndex: "name" },
            { title: "Status", dataIndex: "status" },
            {
              title: "Effective commission",
              dataIndex: "effectivePercent",
              render: (rate: number) => `${rate}%`,
            },
            {
              title: "Rate source",
              dataIndex: "source",
              render: (source: MembershipFee["source"]) => (
                <Tag color={source === "membership" ? "blue" : undefined}>
                  {source === "membership"
                    ? "Membership override"
                    : source === "club"
                      ? "Club default"
                      : "Platform default"}
                </Tag>
              ),
            },
            ...(identity?.role === "SUPER_ADMIN"
              ? [
                  {
                    title: "",
                    key: "edit",
                    render: (_: unknown, plan: MembershipFee) => (
                      <Button
                        onClick={() => {
                          setSaveError(null);
                          setEditing(plan);
                          form.setFieldsValue({
                            seasonFeePercent:
                              plan.seasonFeePercent ?? plan.effectivePercent,
                          });
                        }}
                      >
                        Edit fee
                      </Button>
                    ),
                  },
                ]
              : []),
          ]}
        />
      </Space>
      <Modal
        title={`Edit commission — ${editing?.name ?? ""}`}
        open={!!editing}
        onCancel={() => {
          if (!saving) setEditing(null);
        }}
        footer={null}
        destroyOnClose
      >
        {saveError && (
          <Alert
            type="error"
            showIcon
            message={saveError}
            style={{ marginBottom: 16 }}
          />
        )}
        <Form
          form={form}
          layout="vertical"
          onFinish={(values) => save(values.seasonFeePercent)}
        >
          <Form.Item
            name="seasonFeePercent"
            label="Membership commission (%)"
            rules={[
              {
                required: true,
                message: "Enter a percentage or use the club default below",
              },
              { type: "number", min: 0, max: 100 },
              {
                validator: (_, value) =>
                  value == null ||
                  Math.abs(value * 100 - Math.round(value * 100)) < 1e-8
                    ? Promise.resolve()
                    : Promise.reject(
                        new Error("Use at most two decimal places"),
                      ),
              },
            ]}
          >
            <InputNumber
              min={0}
              max={100}
              step={0.01}
              addonAfter="%"
              style={{ width: "100%" }}
            />
          </Form.Item>
          <Space wrap>
            <Button type="primary" htmlType="submit" loading={saving}>
              Save override
            </Button>
            <Button disabled={saving} onClick={() => save(null)}>
              Use club default ({fees?.inheritedPercent}%)
            </Button>
          </Space>
        </Form>
      </Modal>
    </Card>
  );
}
